import { defineStore } from 'pinia';

import { mapToApiClient } from '@/features/Clients/ClientMapper/clientMapper';
import type {
  ClientResource,
  ClientResourceContainer,
  Role,
} from '@/features/Clients/types';
import {
  ANREDE_FIRMA,
  ClientResourcePWApi,
  OPTIONAL_ROLE,
  ROLE,
} from '@/features/Clients/types';
import {
  buildClientFromApiResponse,
  createClientResourceContainer,
  dedupeAbweichenderHalter,
  getDefaultClientResource,
} from '@/features/Clients/utils';
import { applyQuoteDataToExistingEntity } from '@/features/Quotes/utils';
import type { HttpClientResponse } from '@/services/api/HttpClient';
import { useApi } from '@/services/api/useApi';
import { VGL_ERROR_CODE } from '@/services/errorHandler/errorCodes';
import { getErrorMessage } from '@/services/errorHandler/utils';
import { VglError } from '@/services/errorHandler/vglError';
import { getCurrentSpartenConfig } from '@/utils/getCurrentSpartenConfig/getCurrentSpartenConfig';
import { useStorableState } from '@/utils/useStorableState/useStorableState';
import { CanceledError } from 'axios';
import set from 'lodash/set';
import { useInputStore } from '@/features/Sparten';
import get from 'lodash/get';
import BackendValidationError from '@/features/validation/backendValidation';
import { generateUUID } from '@/utils/uuid/uuid';
import { useQuoteStore } from '@/features/Quotes/store';
import cloneDeep from 'lodash/cloneDeep';
import { getEntries } from '@/utils/objects/objects';
import { clientSchema } from '@/features/Clients/Schemas/Base';
import { hasRelationshipWithAPartner } from '@/features/Sparten/shared/renderConditions/property/sharedRenderConditions';
import { AIResponse } from '@/features/AI/types';
import { useClient } from '@/features/Clients/useClient';
import { EntityStore } from '@/features/EntityManager/types';
import dot from 'dot-object';
import { isDate, parseISODate } from '@/utils/dates/dates';
import { dehydrateRequestData } from '@/utils/dehydrateRequestData/dehydrateRequestData';
import { AnySchema } from 'yup';

export interface ClientStoreState extends EntityStore<ClientResourceContainer> {
  persistedState: Map<number, ClientResource>;
  runningSearch: {
    request: HttpClientResponse<ClientResourcePWApi[]> | null;
    abortController: AbortController | null;
    uuid: string;
  };
}

export const useClientStore = defineStore('ClientStore', {
  state: (): ClientStoreState => ({
    entities: [
      createClientResourceContainer(),
      createClientResourceContainer([ROLE.MITVERSICHERTER_PARTNER]),
    ],
    persistedState: new Map<number, ClientResource>(),
    runningSearch: {
      request: null,
      abortController: null,
      uuid: '',
    },
  }),

  actions: {
    setLoadingStateIfClientAlreadyExists(role: Role, isLoading: boolean) {
      try {
        const client = this.getClientByRole(role);
        client.isLoading = isLoading;
      } catch {
        // optional roles may not be added to the client entities yet - so we don`t care about the loading state for now
      }
    },

    async loadClientById(
      id: string | number,
      roles?: Role[],
      transferToInputStore: boolean = false,
    ) {
      try {
        if (roles !== undefined && roles.length > 0) {
          this.setLoadingStateIfClientAlreadyExists(roles[0], true);
        } else {
          this.setLoadingStateIfClientAlreadyExists(
            ROLE.VERSICHERUNGSNEHMER,
            true,
          );
        }

        const clientApi = useApi().client;
        const [, request] = clientApi.loadClientById(id);

        const result = await request;
        if (result === null) {
          throw new VglError(VGL_ERROR_CODE.GENERAL_UNEXPECTED_EMPTY_RESPONSE, {
            additionalErrorMessage: 'client -> loadClientById',
          });
        }

        // clean up client from api as it could contain extra fields
        const client = buildClientFromApiResponse(result);

        const { clientEntityMetaSchema } = getCurrentSpartenConfig();

        if (clientEntityMetaSchema !== undefined) {
          let metaSchema: AnySchema | undefined;
          roles?.forEach((role) => {
            if (clientEntityMetaSchema[role] !== undefined) {
              metaSchema = clientEntityMetaSchema[role];
            }
          });

          if (metaSchema !== undefined) {
            set(client, 'meta', metaSchema.getDefault());
          }
        }

        if (roles && roles.length > 0) {
          this.addNewClient(roles, true, client);
        } else {
          this.changeClientByRole(ROLE.VERSICHERUNGSNEHMER, client);
        }

        // store the original data to detect changes on step4 to identify overrides
        this.persistedState.set(Number(client.id), client);

        // transfer specified information to the input store
        // required in Sparten like HAU where some address information won`t be saved in the client scope
        const keepDataFromQuoteDefinition =
          getCurrentSpartenConfig().keepDataFromQuote;
        const clientFieldsToTransferToInputStore =
          keepDataFromQuoteDefinition?.copyClientFieldsToInputStore;

        if (
          transferToInputStore &&
          clientFieldsToTransferToInputStore !== undefined
        ) {
          const inputStore = useInputStore();

          getEntries(clientFieldsToTransferToInputStore).forEach(
            ([clientFieldPath, inputStorePath]) => {
              // only override when current state is empty, to avoid override of quote data
              if (get(inputStore, inputStorePath) === '') {
                set(inputStore, inputStorePath, get(client, clientFieldPath));
              }
            },
          );
        }

        return client.id;
      } catch (error) {
        throw new VglError(VGL_ERROR_CODE.CLIENTS_LOADING_FAILED, {
          additionalErrorMessage: `${getErrorMessage(
            error,
          )} - ClientId: ${id} - Roles: ${(roles ?? []).join(',')}`,
          cause: error,
        });
      } finally {
        if (roles !== undefined && roles.length > 0) {
          this.setLoadingStateIfClientAlreadyExists(roles[0], false);
        } else {
          this.setLoadingStateIfClientAlreadyExists(
            ROLE.VERSICHERUNGSNEHMER,
            false,
          );
        }
      }
    },

    async searchClientByName(query: string) {
      // check if we have a running search and cancel it
      if (this.runningSearch !== null) {
        const { request, abortController } = this.runningSearch;
        if (request !== null) {
          const [cancelSearch] = request;
          cancelSearch();
        }

        if (abortController !== null) {
          abortController.abort();
        }

        this.runningSearch.uuid = '';
      }

      const clientApi = useApi().client;

      const abortController = new AbortController();

      const request = clientApi.searchClientByName(query);

      const uuid = generateUUID();

      this.runningSearch = {
        request,
        abortController,
        uuid,
      };

      const task = new Promise<ClientResourcePWApi[]>((resolve, reject) => {
        const abortListener = () => {
          abortController.signal.removeEventListener('abort', abortListener);
          reject(new CanceledError());
        };

        abortController.signal.addEventListener('abort', abortListener);

        const [, resultPromise] = request;

        resultPromise
          .then((result) => {
            if (result === null) {
              resolve([]);
            } else {
              resolve(result);
            }
          })
          .catch((e) => {
            if (!(e instanceof CanceledError)) {
              throw e;
            }
          })
          .finally(() => {
            // reset running search when uuid matches
            if (uuid === this.runningSearch.uuid) {
              this.runningSearch = {
                request: null,
                abortController: null,
                uuid: '',
              };
            }
          });
      });

      // return a promise that resolves our request promise
      return task;
    },

    async saveClient(role: Role, client: ClientResource) {
      try {
        const clientApi = useApi().client;
        this.getClientByRole(role).isLoading = true;
        const mappedClient = mapToApiClient(client);

        const [, request] = clientApi.saveClient(mappedClient, client.id);

        const clientData = await request;

        if (clientData === null) {
          throw new VglError(VGL_ERROR_CODE.GENERAL_UNEXPECTED_EMPTY_RESPONSE, {
            additionalErrorMessage: 'client -> saveClient',
          });
        }

        if (client.id === null) {
          this.setIdByRole(role, clientData.id);
        }
      } catch (error) {
        if (error instanceof BackendValidationError) {
          throw error;
        }

        throw new VglError(VGL_ERROR_CODE.CLIENTS_SAVING_FAILED, {
          additionalErrorMessage: `${getErrorMessage(
            error,
          )} - Role: ${role} - Client: ${JSON.stringify(client)}`,
          cause: error,
        });
      } finally {
        this.getClientByRole(role).isLoading = false;
      }
    },

    setClientDataByRole(role: Role, data: ClientResource) {
      const index = this.entities.findIndex((client) =>
        client.roles.includes(role),
      );

      this.entities[index].data = data;
      this.entities[index].isDirty = true;
    },

    setClientDataByRefId(refId: number, data: ClientResource) {
      const index = this.entities.findIndex((client) => client.refId === refId);

      this.entities[index].data = data;
      this.entities[index].isDirty = true;
    },

    setIdByRole(role: Role, id: number) {
      const index = this.entities.findIndex((client) =>
        client.roles.includes(role),
      );

      this.entities[index].data.id = id;
      this.entities[index].isDirty = false;
    },

    moveRoleFromClient(roleToMove: Role, successorRole: Role) {
      const successorIndex = this.entities.findIndex((client) =>
        client.roles.includes(successorRole),
      );
      const predecessorIndex = this.entities.findIndex((client) =>
        client.roles.includes(roleToMove),
      );

      this.entities[successorIndex].roles.push(roleToMove);

      const filteredRoles = this.entities[predecessorIndex].roles.filter(
        (role) => role !== roleToMove,
      );

      if (filteredRoles.length === 0) {
        this.entities.splice(predecessorIndex, 1);
      } else {
        this.entities[predecessorIndex].roles = filteredRoles;
      }
    },

    isSameByRole(roleA: Role, roleB: Role) {
      const clientAIndex = this.entities.findIndex((client) =>
        client.roles.includes(roleA),
      );
      const clientBIndex = this.entities.findIndex((client) =>
        client.roles.includes(roleB),
      );

      return clientAIndex === clientBIndex;
    },

    addNewClient(
      roles: Role[],
      removeRoleFromOthers = false,
      newClient: ClientResource | undefined = undefined,
      isDirty = false,
    ) {
      const newEntities = cloneDeep(this.entities);

      roles.forEach((role) => {
        if (
          role === ROLE.VERSICHERUNGSNEHMER ||
          role === ROLE.ZAHLPERSON ||
          removeRoleFromOthers
        ) {
          const index = newEntities.findIndex((client) =>
            client.roles.includes(role),
          );

          if (index === -1) return; // could happen if we have some OPTIONAL_ROLES

          const filteredRoles = newEntities[index].roles.filter(
            (val) => val !== role,
          );

          if (filteredRoles.length === 0) {
            newEntities.splice(index, 1);
          } else {
            newEntities[index].roles = filteredRoles;
          }
        }
      });

      newEntities.push(
        createClientResourceContainer(roles, newClient, isDirty),
      );

      this.entities = newEntities;

      if (
        roles.includes(ROLE.ZAHLPERSON) &&
        !roles.includes(ROLE.VERSICHERUNGSNEHMER) &&
        this.versicherungsnehmer.isDirty
      ) {
        this.clearBankInformationFromVN();
      }
    },

    clearBankInformationFromVN() {
      this.versicherungsnehmer.data.bankverbindung =
        clientSchema.getDefault().bankverbindung;
    },

    changeClientByRole(role: Role, client: ClientResource) {
      const cleanedClient = buildClientFromApiResponse(client);

      const index = this.entities.findIndex((c) => c.roles.includes(role));
      const { roles } = this.entities[index];

      this.entities[index] = createClientResourceContainer(
        roles,
        cleanedClient,
      );

      // store the original data to detect changes on step4 to identify overrides
      if (cleanedClient.id !== null) {
        this.persistedState.set(cleanedClient.id, cleanedClient);
      }
    },

    removeClientByRole(role: Role, hard: boolean = false) {
      if (hard) {
        this.entities = this.entities.filter((e) => !e.roles.includes(role));
      } else {
        const index = this.entities.findIndex((client) =>
          client.roles.includes(role),
        );

        const { roles } = this.entities[index];

        this.entities[index] = createClientResourceContainer(roles);
      }
    },

    saveClientIfDirty(role: Role) {
      const client = this.getClientByRole(role);

      if (client.isDirty) {
        return this.saveClient(role, client.data).catch((error) => {
          if (error instanceof BackendValidationError) {
            throw error;
          }

          throw new VglError(VGL_ERROR_CODE.CLIENTS_SAVING_FAILED, {
            additionalErrorMessage: `${getErrorMessage(
              error,
            )} - Role: ${role} - Client: ${JSON.stringify(client)}`,
          });
        });
      }
      return Promise.resolve(null);
    },

    loadClientsFromQuoteEntities(
      entities: ClientResourceContainer[],
      keepVNDataFromQuote = true,
      skipLoadingClient = false,
    ) {
      const keepDataFromQuoteDefinition =
        getCurrentSpartenConfig().keepDataFromQuote;
      const clientKeepFromQuoteDefinition =
        keepDataFromQuoteDefinition?.clients;

      dedupeAbweichenderHalter(entities).forEach((clientContainer) => {
        const clientId = clientContainer.data.id;
        const { roles } = clientContainer;

        if (clientId !== undefined && clientId !== null && !skipLoadingClient) {
          this.loadClientById(clientId, roles, true).then((actualClientId) => {
            if (
              keepVNDataFromQuote &&
              clientKeepFromQuoteDefinition !== undefined
            ) {
              // check for dublette and apply new client id to the clientContainer
              if (actualClientId !== null && clientId !== actualClientId) {
                clientContainer.data.id = actualClientId;
                // also check for quote data
                const quoteStore = useQuoteStore();
                if (quoteStore.data !== null) {
                  if (quoteStore.data.clientId === clientId) {
                    quoteStore.data.clientId = actualClientId;
                  }
                }
              }

              roles.forEach((role) => {
                const mappings = clientKeepFromQuoteDefinition[role];
                if (mappings !== undefined) {
                  applyQuoteDataToExistingEntity(
                    clientContainer,
                    role,
                    mappings,
                  );
                }
              });
            }
          });
        } else {
          // also load clients from quote that aren't clients from PW
          this.addNewClient(
            roles,
            true,
            useStorableState().read(clientContainer.data),
            clientContainer.isDirty,
          );
        }
      });
    },

    async loadClientsFromAIResponse(
      clients: AIResponse['clients'] = [],
    ): Promise<string[]> {
      const result: string[] = [];
      if (clients.length > 0) {
        const clientStore = useClientStore();
        const { clientEntityMetaSchema } = getCurrentSpartenConfig();

        clients.forEach((clientData) => {
          let usedClient;
          let currentRole: Role | null = null;

          switch (clientData.rolle) {
            case ROLE.VERSICHERUNGSNEHMER:
              usedClient = useClient(ROLE.VERSICHERUNGSNEHMER);
              currentRole = ROLE.VERSICHERUNGSNEHMER;
              break;
            case OPTIONAL_ROLE.ABWEICHENDER_HALTER:
              if (
                !clientStore.hasClientWithRole(
                  OPTIONAL_ROLE.ABWEICHENDER_HALTER,
                )
              ) {
                clientStore.addNewClient([OPTIONAL_ROLE.ABWEICHENDER_HALTER]);
              }
              usedClient = useClient(OPTIONAL_ROLE.ABWEICHENDER_HALTER);
              currentRole = OPTIONAL_ROLE.ABWEICHENDER_HALTER;
              break;
            case ROLE.MITVERSICHERTER_PARTNER:
              if (
                !clientStore.hasClientWithRole(ROLE.MITVERSICHERTER_PARTNER)
              ) {
                clientStore.addNewClient([ROLE.MITVERSICHERTER_PARTNER]);
              }
              usedClient = useClient(ROLE.MITVERSICHERTER_PARTNER);
              usedClient.client.value.isDirty = true;
              currentRole = ROLE.MITVERSICHERTER_PARTNER;
              break;
            case ROLE.VERSICHERTE_PERSON:
              if (
                !clientStore.hasClientWithRole(ROLE.VERSICHERTE_PERSON) ||
                clientStore.isSameByRole(
                  ROLE.VERSICHERUNGSNEHMER,
                  ROLE.VERSICHERTE_PERSON,
                )
              ) {
                clientStore.addNewClient([ROLE.VERSICHERTE_PERSON], true);
              }
              usedClient = useClient(ROLE.VERSICHERTE_PERSON);
              usedClient.client.value.isDirty = true;
              currentRole = ROLE.VERSICHERTE_PERSON;
              break;
            case OPTIONAL_ROLE.VERSICHERTE_PERSON_2:
            case OPTIONAL_ROLE.VERSICHERTE_PERSON_3:
            case OPTIONAL_ROLE.VERSICHERTE_PERSON_4:
            case OPTIONAL_ROLE.VERSICHERTE_PERSON_5:
            case OPTIONAL_ROLE.VERSICHERTE_PERSON_6: {
              const versichertePersonRole = clientData.rolle as OPTIONAL_ROLE;

              if (!clientStore.hasClientWithRole(versichertePersonRole)) {
                clientStore.addNewClient([versichertePersonRole]);
              }

              usedClient = useClient(versichertePersonRole);
              usedClient.client.value.isDirty = true;
              currentRole = versichertePersonRole;
              break;
            }
            case OPTIONAL_ROLE.FAHRER_1:
            case OPTIONAL_ROLE.FAHRER_2:
            case OPTIONAL_ROLE.FAHRER_3:
            case OPTIONAL_ROLE.FAHRER_4:
            case OPTIONAL_ROLE.FAHRER_5: {
              const fahrerRole = clientData.rolle as OPTIONAL_ROLE;

              // Prüfen, ob ein Meta-Schema für diese Rolle existiert
              if (
                clientEntityMetaSchema &&
                clientEntityMetaSchema[fahrerRole]
              ) {
                const metaSchema = clientEntityMetaSchema[fahrerRole];
                if (metaSchema) {
                  const defaultMeta = metaSchema.getDefault();

                  // Falls noch kein Client für diese Rolle existiert, einen neuen mit vorbereiteten Meta-Daten erstellen
                  if (!clientStore.hasClientWithRole(fahrerRole)) {
                    const clientToAdd = getDefaultClientResource();

                    set(clientToAdd, 'meta', defaultMeta);

                    clientStore.addNewClient([fahrerRole], true, clientToAdd);
                  }

                  usedClient = useClient(fahrerRole);
                  currentRole = fahrerRole;

                  const clientDataValue = usedClient.client.value.data;

                  const existingMeta = get(clientDataValue, 'meta') || {};

                  const aiMeta =
                    'meta' in clientData && clientData.meta
                      ? clientData.meta
                      : {};

                  const combinedMeta = {
                    ...defaultMeta,
                    ...existingMeta,
                    ...aiMeta,
                  };

                  set(clientDataValue, 'meta', combinedMeta);
                }
              } else {
                usedClient = useClient(fahrerRole);
                currentRole = fahrerRole;
              }
              break;
            }
            default:
              return;
          }

          if (usedClient && !usedClient.isPersistedClient.value) {
            const { rolle, ...strippedClientData } = clientData;

            const dateFields = ['fuehrerscheindatum', 'geburtsdatum'] as const;
            dateFields.forEach((field) => {
              const value = strippedClientData[field];
              if (
                value !== null &&
                typeof value === 'string' &&
                !isDate(value)
              ) {
                strippedClientData[field] = parseISODate(value);
              }
            });

            // Originale Meta-Daten sichern
            const originalMeta = get(usedClient.client.value.data, 'meta');

            // Meta aus den strippedClientData entfernen, um ein Überschreiben zu verhindern
            const { meta: _, ...strippedClientDataWithoutMeta } =
              strippedClientData as any;

            // Client-Daten ohne Meta-Daten aktualisieren
            usedClient.client.value.data = {
              ...usedClient.client.value.data,
              ...strippedClientDataWithoutMeta,
            };

            // Prüfen, ob Meta-Daten verloren gegangen sind und ggf. wiederherstellen
            const newMeta = get(usedClient.client.value.data, 'meta');
            if (originalMeta && !newMeta) {
              set(usedClient.client.value.data, 'meta', originalMeta);
            }

            if (currentRole) {
              Object.keys(dot.dot(strippedClientData)).forEach(
                (field: string) => {
                  result.push(`${currentRole}-${field}`);
                },
              );
            }
          }
        });
      }

      return result;
    },

    deleteEntityByRefId(refId: number) {
      this.entities = this.entities.filter((entity) => entity.refId !== refId);
    },

    getDataByRole(role: Role) {
      try {
        return this.getClientByRole(role).data;
      } catch (e) {
        return undefined;
      }
    },
  },

  getters: {
    // We assume it's impossible to not find a client for the given role.
    // Because of this we throw an exception if no client is found for the role.
    // This way we also can say we will ALWAYS return a ClientResourceContainer
    [ROLE.VERSICHERUNGSNEHMER](state): ClientResourceContainer {
      const vn = state.entities.find((client) =>
        client.roles.includes(ROLE.VERSICHERUNGSNEHMER),
      );
      if (vn === undefined) {
        throw new VglError(VGL_ERROR_CODE.CLIENTS_MISSING_VN);
      }
      return vn;
    },

    [ROLE.VERSICHERTE_PERSON](state): ClientResourceContainer {
      const vp = state.entities.find((client) =>
        client.roles.includes(ROLE.VERSICHERTE_PERSON),
      );

      if (vp === undefined) {
        throw new VglError(VGL_ERROR_CODE.CLIENTS_MISSING_VP);
      }
      return vp;
    },

    [ROLE.ZAHLPERSON](state): ClientResourceContainer {
      const zp = state.entities.find((client) =>
        client.roles.includes(ROLE.ZAHLPERSON),
      );
      if (zp === undefined) {
        throw new VglError(VGL_ERROR_CODE.CLIENTS_MISSING_ZP);
      }
      return zp;
    },

    [ROLE.MITVERSICHERTER_PARTNER](state): ClientResourceContainer {
      const mp = state.entities.find((client) =>
        client.roles.includes(ROLE.MITVERSICHERTER_PARTNER),
      );

      if (mp === undefined) {
        throw new VglError(VGL_ERROR_CODE.CLIENTS_MISSING_MP);
      }
      return mp;
    },

    getClientByRefId(state): (refId: number) => ClientResourceContainer {
      return (refId: number) => {
        const client = state.entities.find((c) => c.refId === refId);
        if (client === undefined) {
          throw new VglError(VGL_ERROR_CODE.CLIENTS_GET_BY_REF_ID_FAILED, {
            additionalErrorMessage: `RefId:${refId}`,
          });
        }
        return client;
      };
    },

    vnIsCompany(): boolean {
      return this[ROLE.VERSICHERUNGSNEHMER].data.anredeId === ANREDE_FIRMA;
    },

    // Sach-Sparten (blockCompanyClient) sperren Berechnung und Online-Antrag für Firmen als VN,
    // weil das Backend sie dort ablehnt (OnlineValidator, Anrede::FIRMA)
    vnIsBlockedCompany(): boolean {
      return (
        getCurrentSpartenConfig().blockCompanyClient === true &&
        this.vnIsCompany
      );
    },

    getClientByRole(
      state,
    ): <T = ClientResource>(role: Role) => ClientResourceContainer<T> {
      return <T = ClientResource>(role: Role) => {
        const entity = state.entities.find((client) =>
          client.roles.includes(role),
        );

        if (entity === undefined) {
          throw new VglError(VGL_ERROR_CODE.CLIENTS_MISSING_ENTITY, {
            additionalData: `Role: ${role}`,
          });
        }

        return entity as ClientResourceContainer<T>;
      };
    },

    hasClientWithRole(state): (role: Role) => boolean {
      return (role) => {
        return (
          state.entities.find((c) => {
            return c.roles.includes(role);
          }) !== undefined
        );
      };
    },

    getUsedClientIds(state): number[] {
      const uniqueIds = new Set<number>();

      state.entities.forEach((entity) => {
        if (entity.data.id !== null) {
          uniqueIds.add(entity.data.id);
        }
      });

      return Array.from(uniqueIds);
    },

    getEntityRequestData(state) {
      const relevantEntities: ClientResourceContainer[] = [];
      const config = getCurrentSpartenConfig();

      state.entities.forEach((entity) => {
        // https://demvsystems.atlassian.net/browse/VER-3605
        // skip clients with empty roles -> migration error
        if (entity.roles.length === 0) {
          return;
        }

        if (
          entity.roles.includes(ROLE.MITVERSICHERTER_PARTNER) &&
          (config.supportMitversichertenPartner === undefined ||
            !config.supportMitversichertenPartner ||
            (entity.data.id === null && !entity.isDirty) ||
            (config.supportMitversichertenPartner &&
              !hasRelationshipWithAPartner.value))
        ) {
          return;
        }

        const { clientEntityDehydration } = config;
        if (clientEntityDehydration !== undefined) {
          const entityClone = cloneDeep(entity);
          entity.roles.forEach((role) => {
            const dehydrationConfig = clientEntityDehydration[role];
            if (dehydrationConfig !== undefined) {
              entityClone.data = dehydrateRequestData<ClientResource>(
                entityClone.data,
                dehydrationConfig,
              );
            }
          });

          relevantEntities.push(entityClone);
        } else {
          relevantEntities.push(entity);
        }
      });

      return relevantEntities;
    },
  },
});
